import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon, { HelpHint } from '../common/SafeIcon';
import {
  FiKey, FiPlus, FiSearch, FiCopy, FiRefreshCw, FiCalendar,
  FiPause, FiPlay, FiX, FiShield, FiEdit3, FiTrash2, FiSave,
  FiAlertCircle, FiCheckCircle
} from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const blankPolicy = {
  id: '',
  name: '',
  clientId: '',
  appId: '',
  environment: 'live',
  status: 'active',
  validFrom: '',
  validUntil: '',
  includedEmailLimit: 0,
  monthlyLimit: 0,
  annualLimit: 0,
  allowedOrigins: '',
  allowedRecipients: '',
  allowAnyRecipient: false,
  allowDynamicReplyTo: false,
  fromName: '',
  fromEmail: '',
  replyToEmail: '',
  rateLimitPerMinute: 30
};

const APIKeys = () => {
  const [keys, setKeys] = useState([]);
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [policyOpen, setPolicyOpen] = useState(false);
  const [policyMode, setPolicyMode] = useState('create');
  const [policy, setPolicy] = useState(blankPolicy);
  const [rawKey, setRawKey] = useState('');
  const [rawKeyTitle, setRawKeyTitle] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [keysResponse, clientsResponse, appsResponse] = await Promise.all([
        gatewayApi.listApiKeys(),
        gatewayApi.listClients(),
        gatewayApi.listApps()
      ]);
      setKeys(keysResponse.data || []);
      setClients(clientsResponse.data || []);
      setApps(appsResponse.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load API keys.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map(client => [client.id, client.name])),
    [clients]
  );

  const appMap = useMemo(
    () => Object.fromEntries(apps.map(app => [app.id, app.name])),
    [apps]
  );

  const filteredKeys = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return keys;
    return keys.filter(key =>
      [key.name, clientMap[key.clientId], appMap[key.appId], key.status, key.maskedKey]
        .some(value => String(value || '').toLowerCase().includes(term))
    );
  }, [keys, clientMap, appMap, searchTerm]);

  const filteredApps = policy.clientId
    ? apps.filter(app => app.clientId === policy.clientId)
    : apps;

  const openGenerate = () => {
    const clientId = clients[0]?.id || '';
    const firstApp = apps.find(app => app.clientId === clientId);
    setPolicy({
      ...blankPolicy,
      clientId,
      appId: firstApp?.id || '',
      validFrom: new Date().toISOString().slice(0, 10)
    });
    setPolicyMode('create');
    setPolicyOpen(true);
  };

  const openEdit = (key) => {
    setPolicy({
      id: key.id,
      name: key.name || '',
      clientId: key.clientId || '',
      appId: key.appId || '',
      environment: key.environment || 'live',
      status: key.status || 'active',
      validFrom: String(key.validFrom || '').slice(0, 10),
      validUntil: String(key.validUntil || '').slice(0, 10),
      includedEmailLimit: key.includedEmailLimit || 0,
      monthlyLimit: key.monthlyLimit || 0,
      annualLimit: key.annualLimit || 0,
      allowedOrigins: key.allowedOrigins || '',
      allowedRecipients: key.allowedRecipients || '',
      allowAnyRecipient: Boolean(key.allowAnyRecipient),
      allowDynamicReplyTo: Boolean(key.allowDynamicReplyTo),
      fromName: key.fromName || '',
      fromEmail: key.fromEmail || '',
      replyToEmail: key.replyToEmail || '',
      rateLimitPerMinute: key.rateLimitPerMinute || 30
    });
    setPolicyMode('edit');
    setPolicyOpen(true);
  };

  const validatePolicy = () => {
    if (!policy.name.trim() || !policy.clientId) return 'Key name and client are required.';
    if (!policy.allowedOrigins.trim()) return 'Allowed origin is required.';
    if (!policy.allowAnyRecipient && !policy.allowedRecipients.trim()) {
      return 'Set allowed recipients or explicitly enable dynamic recipients.';
    }
    if (policy.validFrom && policy.validUntil && policy.validUntil < policy.validFrom) {
      return 'Valid Until cannot be before Valid From.';
    }
    return '';
  };

  const savePolicy = async () => {
    const validation = validatePolicy();
    if (validation) {
      showToast(validation, 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...policy,
        includedEmailLimit: Number(policy.includedEmailLimit) || 0,
        monthlyLimit: Number(policy.monthlyLimit) || 0,
        annualLimit: Number(policy.annualLimit) || 0,
        rateLimitPerMinute: Number(policy.rateLimitPerMinute) || 0
      };

      if (policyMode === 'create') {
        const response = await gatewayApi.generateApiKey(payload);
        setRawKey(response.data.rawKey);
        setRawKeyTitle(policy.name);
        showToast('API key generated. Copy it now.');
      } else {
        await gatewayApi.updateApiKey(payload);
        showToast('API key policy updated.');
      }

      setPolicyOpen(false);
      await loadData();
    } catch (err) {
      showToast(err.message || 'Unable to save API key.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (key) => {
    try {
      const next = key.status === 'active' ? 'paused' : 'active';
      await gatewayApi.setApiKeyStatus(key.id, next);
      showToast(`API key ${next === 'active' ? 'activated' : 'paused'}.`);
      await loadData();
    } catch (err) {
      showToast(err.message || 'Unable to update API key status.', 'error');
    }
  };

  const rotate = async (key) => {
    if (!window.confirm('Rotate this key now? The existing key will stop working immediately.')) return;
    try {
      const response = await gatewayApi.rotateApiKey(key.id);
      setRawKey(response.data.rawKey);
      setRawKeyTitle(`${key.name} replacement`);
      showToast('API key rotated.');
      await loadData();
    } catch (err) {
      showToast(err.message || 'Unable to rotate API key.', 'error');
    }
  };

  const revoke = async (key) => {
    if (!window.confirm('Revoke this key permanently? It will no longer be accepted by the gateway.')) return;
    try {
      await gatewayApi.revokeApiKey(key.id);
      showToast('API key revoked.', 'warning');
      await loadData();
    } catch (err) {
      showToast(err.message || 'Unable to revoke API key.', 'error');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">API Access Keys</h2>
          <p className="text-sm text-slate-500">BPA-issued client credentials backed by PocketBase and enforced by the gateway.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/secure-vault" className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-bold text-blue-700 hover:bg-blue-100"><SafeIcon icon={FiShield} /> Secure Vault</Link>
          <button onClick={loadData} className="px-3 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50">
            <SafeIcon icon={FiRefreshCw} />
          </button>
          <Link to="/guided-setup" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-blue-700"><SafeIcon icon={FiPlus} /> Set up business + app + key</Link>
          <button onClick={openGenerate} disabled={!clients.length} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-slate-700 disabled:opacity-50">Standalone key (advanced)</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {[
          ['1', 'Open Quick Setup', 'Enter the business once or select an existing business.'],
          ['2', 'Choose application', 'Create a new app or reuse one already registered.'],
          ['3', 'Set everything up', 'Automatically prepare the app, key, safe permissions and template.'],
          ['4', 'Connect backend', 'Store the key in the target backend and copy its integration SOP.']
        ].map(([step, title, description]) => (
          <div key={step} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="w-7 h-7 rounded-lg bg-slate-900 text-white text-xs font-black flex items-center justify-center">{step}</div>
            <p className="text-sm font-bold text-slate-900 mt-3">{title}</p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{description}</p>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-4">
        <SafeIcon icon={FiShield} className="text-blue-600 text-xl mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-bold text-blue-900">Secure, recoverable API keys</p>
          <p className="text-xs text-blue-800 mt-1 leading-relaxed">
            New BPA keys are encrypted in Secure Vault and can be revealed again by an authorised administrator. PocketBase stores only the key hash and a safe prefix. Raw keys cannot be re-viewed later.
            <b> These BPA keys are generated independently from your Emailit provider key.</b>
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
          <div>
            <p className="font-bold">API key management is not available yet</p>
            <p className="mt-1">{error}</p>
            <p className="mt-2 text-xs text-red-600">
              Your Greta → Netlify deployment publishes the frontend only. API key generation and email delivery require the PocketHost email runtime to be installed once on the PocketBase instance.
            </p>
          </div>
        </div>
      )}

      <div className="flex bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2">
          <SafeIcon icon={FiSearch} className="text-slate-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search keys, clients or apps..."
            className="w-full text-sm outline-none bg-transparent py-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
            Loading API keys from PocketBase...
          </div>
        ) : filteredKeys.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400">
            No API keys found.
          </div>
        ) : filteredKeys.map(key => (
          <div key={key.id} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 grid grid-cols-1 xl:grid-cols-[minmax(260px,1fr)_minmax(300px,1.2fr)_auto] gap-5 items-center">
              <div className="flex items-center gap-4 min-w-0">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0 ${key.status === 'active' ? 'bg-emerald-50 text-emerald-600' : key.status === 'revoked' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>
                  <SafeIcon icon={FiKey} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-900 truncate">{key.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${key.environment === 'test' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {key.environment}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{clientMap[key.clientId] || 'Unknown Client'} • {appMap[key.appId] || 'Standalone'}</p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl border border-slate-100 p-3">
                <p className="font-mono text-xs text-slate-600 break-all">{key.maskedKey || key.keyPrefix || 'Legacy key: rotate required'}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-[10px]">
                  <Mini label="Status" value={key.status} />
                  <Mini label="Valid Until" value={key.validUntil || 'No expiry'} />
                  <Mini label="Monthly Limit" value={Number(key.monthlyLimit || 0).toLocaleString()} />
                  <Mini label="Last Used" value={key.lastUsed ? new Date(key.lastUsed).toLocaleString('en-AU') : 'Never'} />
                </div>
              </div>

              <div className="flex items-center gap-2 justify-end">
                {key.status !== 'revoked' && (
                  <button onClick={() => setStatus(key)} className="p-2.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-amber-50 hover:text-amber-600" title={key.status === 'active' ? 'Pause' : 'Activate'}>
                    <SafeIcon icon={key.status === 'active' ? FiPause : FiPlay} />
                  </button>
                )}
                <button onClick={() => openEdit(key)} className="p-2.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-blue-50 hover:text-blue-600" title="Edit policy">
                  <SafeIcon icon={FiEdit3} />
                </button>
                {key.status !== 'revoked' && (
                  <button onClick={() => rotate(key)} className="p-2.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-blue-50 hover:text-blue-600" title="Rotate key">
                    <SafeIcon icon={FiRefreshCw} />
                  </button>
                )}
                {key.status !== 'revoked' && (
                  <button onClick={() => revoke(key)} className="p-2.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Revoke key">
                    <SafeIcon icon={FiTrash2} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {policyOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden max-h-[94vh] flex flex-col">
            <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="font-bold">{policyMode === 'create' ? 'Generate BPA API Key' : 'Edit API Key Policy'}</h3>
                <p className="text-xs text-slate-400">Origin, quota, recipient and sender rules are enforced before every send.</p>
              </div>
              <button onClick={() => setPolicyOpen(false)} className="p-1 hover:bg-slate-800 rounded">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Key Name *" value={policy.name} onChange={value => setPolicy({ ...policy, name: value })} />
                <Select label="Client *" value={policy.clientId} onChange={value => {
                  const nextApps = apps.filter(app => app.clientId === value);
                  setPolicy({ ...policy, clientId: value, appId: nextApps[0]?.id || '' });
                }}>
                  {clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}
                </Select>
                <Select label="Linked App" value={policy.appId} onChange={value => setPolicy({ ...policy, appId: value })}>
                  <option value="">Standalone / Client level</option>
                  {filteredApps.map(app => <option key={app.id} value={app.id}>{app.name}</option>)}
                </Select>
                <Select label="Environment" value={policy.environment} onChange={value => setPolicy({ ...policy, environment: value })} disabled={policyMode === 'edit'}>
                  <option value="live">Live</option>
                  <option value="test">Test</option>
                </Select>
                <Field label="Valid From" type="date" value={policy.validFrom} onChange={value => setPolicy({ ...policy, validFrom: value })} />
                <Field label="Valid Until" type="date" value={policy.validUntil} onChange={value => setPolicy({ ...policy, validUntil: value })} />
              </div>

              <section>
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Quota Policy</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Field label="Included Allowance" type="number" value={policy.includedEmailLimit} onChange={value => setPolicy({ ...policy, includedEmailLimit: value })} />
                  <Field label="Monthly Limit" type="number" value={policy.monthlyLimit} onChange={value => setPolicy({ ...policy, monthlyLimit: value })} />
                  <Field label="Annual Limit" type="number" value={policy.annualLimit} onChange={value => setPolicy({ ...policy, annualLimit: value })} />
                  <Field label="Rate / Minute" type="number" value={policy.rateLimitPerMinute} onChange={value => setPolicy({ ...policy, rateLimitPerMinute: value })} />
                </div>
              </section>

              <section className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                <h4 className="text-sm font-bold text-blue-900">Access & Recipient Policy</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Field label="Allowed Origins *" value={policy.allowedOrigins} onChange={value => setPolicy({ ...policy, allowedOrigins: value })} placeholder="https://example.com" />
                  <Field label="Allowed Recipients" value={policy.allowedRecipients} onChange={value => setPolicy({ ...policy, allowedRecipients: value })} placeholder="owner@example.com" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <Check checked={policy.allowAnyRecipient} onChange={checked => setPolicy({ ...policy, allowAnyRecipient: checked })} title="Allow Dynamic Recipients" description="Use for OTP or customer acknowledgements where the recipient changes per request." />
                  <Check checked={policy.allowDynamicReplyTo} onChange={checked => setPolicy({ ...policy, allowDynamicReplyTo: checked })} title="Allow Dynamic Reply-To" description="Use for contact forms where Reply-To should be the visitor's email." />
                </div>
              </section>

              <section>
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Sender Identity Override</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="From Name" value={policy.fromName} onChange={value => setPolicy({ ...policy, fromName: value })} />
                  <Field label="From Email" type="email" value={policy.fromEmail} onChange={value => setPolicy({ ...policy, fromEmail: value })} />
                  <Field label="Default Reply-To" type="email" value={policy.replyToEmail} onChange={value => setPolicy({ ...policy, replyToEmail: value })} />
                </div>
                <p className="text-[10px] text-slate-400 mt-2">Blank values inherit from the linked app, then the client, then the gateway environment defaults.</p>
              </section>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={() => setPolicyOpen(false)} className="px-4 py-2 text-sm font-bold text-slate-500">Cancel</button>
              <button onClick={savePolicy} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : policyMode === 'create' ? 'Generate Key' : 'Save Policy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rawKey && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-7">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <SafeIcon icon={FiCheckCircle} className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mt-4">Copy this key now</h3>
              <p className="text-sm text-slate-500 mt-1">{rawKeyTitle}. The full value will not be stored or shown again.</p>

              <div className="mt-5 bg-slate-950 text-emerald-300 rounded-xl p-4 font-mono text-xs break-all relative">
                {rawKey}
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(rawKey);
                    showToast('API key copied.');
                  }}
                  className="absolute top-2 right-2 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"
                  title="Copy key"
                >
                  <SafeIcon icon={FiCopy} />
                </button>
              </div>

              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-3 text-xs text-amber-800">
                <SafeIcon icon={FiCalendar} className="mt-0.5 shrink-0" />
                Store this key in the client application's Netlify project variable. Do not put the raw provider key in the client app.
              </div>

              <button onClick={() => { setRawKey(''); setRawKeyTitle(''); }} className="mt-6 w-full py-3 bg-slate-900 text-white rounded-xl text-sm font-bold">
                I have saved the key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-50" />
  </div>
);

const Select = ({ label, value, onChange, children, disabled = false }) => (
  <div>
    <div className="flex items-center gap-1"><span className="text-xs font-bold text-slate-500 uppercase">{label}</span><HelpHint label={label}/></div>
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="mt-2 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none disabled:bg-slate-100">
      {children}
    </select>
  </div>
);

const Check = ({ checked, onChange, title, description }) => (
  <div className="flex items-start gap-2"><label className="flex flex-1 gap-3 bg-white border border-blue-100 rounded-xl p-4 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1" />
    <div>
      <p className="text-xs font-bold text-slate-800">{title}</p>
      <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{description}</p>
    </div>
  </label><HelpHint label={title}/></div>
);

const Mini = ({ label, value }) => (
  <div>
    <p className="uppercase tracking-wider text-slate-400 font-bold">{label}</p>
    <p className="font-semibold text-slate-700 truncate">{value}</p>
  </div>
);

export default APIKeys;